/**
 * Chat tab — open/anonymous live match chat. Device-based identity (nickname +
 * optional team badge stored in localStorage), polling, AI translate.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Send, Languages, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REACTIONS = ['🔥', '⚽', '😱'];

const getMyReaction = (msgId) => { try { return localStorage.getItem(`sk_react_${msgId}`) || ''; } catch { return ''; } };
const setMyReaction = (msgId, emoji) => { try { emoji ? localStorage.setItem(`sk_react_${msgId}`, emoji) : localStorage.removeItem(`sk_react_${msgId}`); } catch { /* ignore */ } };

const getDeviceId = () => {
  try {
    let id = localStorage.getItem('sk_chat_device');
    if (!id) { id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('sk_chat_device', id); }
    return id;
  } catch { return 'dev_anon'; }
};
const getNick = () => {
  try { return localStorage.getItem('sk_chat_nick') || ''; } catch { return ''; }
};

const SystemLine = ({ m }) => {
  const red = m.kind === 'red_card';
  return (
    <div className="flex justify-center my-1" data-testid="chat-system-line">
      <div className={`px-4 py-2 rounded-full text-[13px] font-black text-center ${red ? 'bg-red-500/15 text-red-300 border border-red-500/40' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'}`}>
        {m.text}
      </div>
    </div>
  );
};

const Bubble = ({ m, mine, matchId, deviceId }) => {
  const [translated, setTranslated] = useState(null);
  const [showOrig, setShowOrig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState(m.reaction_counts || {});
  const [mineReact, setMineReact] = useState(getMyReaction(m.id));

  const translate = async () => {
    if (translated) { setShowOrig((s) => !s); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/scores/chat/translate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: m.text, target: 'English' }),
      });
      const d = await r.json();
      if (d.translated) setTranslated(d.translated);
      else toast.error('Translation unavailable');
    } catch { toast.error('Translation unavailable'); }
    setLoading(false);
  };

  const react = async (emoji) => {
    try {
      const r = await fetch(`${API_URL}/api/scores/match/${matchId}/chat/${m.id}/react`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId, emoji }),
      });
      const d = await r.json();
      if (d.reaction_counts) setCounts(d.reaction_counts);
      const next = mineReact === emoji ? '' : emoji;
      setMineReact(next); setMyReaction(m.id, next);
    } catch { /* ignore */ }
  };

  return (
    <div className={`flex items-start gap-2.5 ${mine ? 'flex-row-reverse' : ''}`} data-testid="chat-message">
      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 overflow-hidden">
        {m.team_logo ? <img src={m.team_logo} alt="" className="w-5 h-5 object-contain" /> :
          <span className="text-[12px] font-black text-zinc-300">{(m.nickname || 'G')[0].toUpperCase()}</span>}
      </div>
      <div className={`max-w-[76%] ${mine ? 'items-end text-right' : ''} flex flex-col`}>
        <span className="text-[10px] text-zinc-500 mb-0.5 px-1">{m.nickname}</span>
        <div className={`rounded-2xl px-3 py-2 text-[13px] leading-snug ${mine ? 'bg-accent text-white' : 'bg-[var(--sk-surface)] text-zinc-100'}`}>
          {translated && !showOrig ? translated : m.text}
        </div>
        {/* Reactions row */}
        <div className={`flex items-center gap-1 mt-1 ${mine ? 'justify-end' : ''}`}>
          {REACTIONS.map((e) => {
            const c = counts[e] || 0;
            const active = mineReact === e;
            return (
              <button key={e} onClick={() => react(e)} data-testid={`chat-react-${e}`}
                className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] transition-colors ${active ? 'bg-accent/25 ring-1 ring-accent/50' : 'hover:bg-white/5'}`}>
                <span>{e}</span>{c > 0 && <span className="text-zinc-400 font-bold">{c}</span>}
              </button>
            );
          })}
          <button onClick={translate} className="ml-1 px-1 text-[10px] text-zinc-500 hover:text-accent flex items-center gap-1" data-testid="chat-translate-btn">
            {loading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Languages className="w-2.5 h-2.5" />}
            {translated ? (showOrig ? 'Translation' : 'Original') : 'Translate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MatchChat = ({ matchId, homeTeam, awayTeam, homeLogo, awayLogo }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [nick, setNick] = useState(getNick());
  const [teamLogo, setTeamLogo] = useState(() => { try { return localStorage.getItem(`sk_chat_team_${matchId}`) || ''; } catch { return ''; } });
  const [sending, setSending] = useState(false);
  const deviceId = useRef(getDeviceId());
  const lastAt = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async (initial) => {
    try {
      const qs = !initial && lastAt.current ? `?since=${encodeURIComponent(lastAt.current)}` : '';
      const r = await fetch(`${API_URL}/api/scores/match/${matchId}/chat${qs}`);
      const d = await r.json();
      const incoming = d.messages || [];
      if (incoming.length) {
        lastAt.current = incoming[incoming.length - 1].created_at;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...incoming.filter((m) => !seen.has(m.id))];
          return merged.slice(-200);
        });
      }
    } catch { /* keep prev */ }
  }, [matchId]);

  useEffect(() => { load(true); const t = setInterval(() => load(false), 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const chooseTeam = (logo) => {
    const next = teamLogo === logo ? '' : logo;
    setTeamLogo(next);
    try { localStorage.setItem(`sk_chat_team_${matchId}`, next); } catch { /* ignore */ }
  };

  const editNick = () => {
    const n = (window.prompt('Choose a chat name', nick || 'Guest') || '').trim().slice(0, 24);
    if (n) { setNick(n); try { localStorage.setItem('sk_chat_nick', n); } catch { /* ignore */ } }
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    let name = nick;
    if (!name) { name = 'Guest' + Math.floor(1000 + Math.random() * 9000); setNick(name); try { localStorage.setItem('sk_chat_nick', name); } catch { /* ignore */ } }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/scores/match/${matchId}/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId.current, nickname: name, text: t, team_logo: teamLogo || null }),
      });
      if (r.ok) { setText(''); await load(false); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Message not sent'); }
    } catch { toast.error('Message not sent'); }
    setSending(false);
  };

  return (
    <div className="flex flex-col" style={{ minHeight: '60vh' }} data-testid="chat-panel">
      {/* Identity bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--sk-border)]">
        <button onClick={editNick} className="flex items-center gap-1.5 text-[12px] text-zinc-300" data-testid="chat-edit-nick">
          <span className="text-zinc-500">Chatting as</span>
          <span className="font-bold text-white">{nick || 'Guest'}</span>
          <Pencil className="w-3 h-3 text-zinc-500" />
        </button>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500">Badge:</span>
          {homeLogo && <button onClick={() => chooseTeam(homeLogo)} className={`w-7 h-7 rounded-full flex items-center justify-center ${teamLogo === homeLogo ? 'ring-2 ring-accent' : 'opacity-60'}`} data-testid="chat-badge-home"><img src={homeLogo} alt={homeTeam} className="w-5 h-5 object-contain" /></button>}
          {awayLogo && <button onClick={() => chooseTeam(awayLogo)} className={`w-7 h-7 rounded-full flex items-center justify-center ${teamLogo === awayLogo ? 'ring-2 ring-accent' : 'opacity-60'}`} data-testid="chat-badge-away"><img src={awayLogo} alt={awayTeam} className="w-5 h-5 object-contain" /></button>}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ maxHeight: '60vh' }} data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-center text-[12px] text-zinc-500 py-10">Be the first to say something — no sign-up needed. Keep it friendly! 🟢</div>
        ) : messages.map((m) => (
          m.system
            ? <SystemLine key={m.id} m={m} />
            : <Bubble key={m.id} m={m} mine={m.device_id === deviceId.current} matchId={matchId} deviceId={deviceId.current} />
        ))}
      </div>

      {/* Composer */}
      <div className="sticky bottom-0 bg-[var(--sk-bg)]/95 backdrop-blur border-t border-[var(--sk-border)] px-3 py-2.5 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Type chat……"
          maxLength={280}
          className="flex-1 bg-white/5 rounded-full px-4 py-2.5 text-[14px] text-white placeholder-zinc-500 outline-none focus:bg-white/10"
          data-testid="chat-input"
        />
        <button onClick={send} disabled={sending || !text.trim()} className="w-10 h-10 rounded-full bg-accent flex items-center justify-center disabled:opacity-40" data-testid="chat-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-white" />}
        </button>
      </div>
    </div>
  );
};

export default MatchChat;
